/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import moment from 'moment-timezone';
import {
    forEach,
    get,
    map,
    merge,
    union,
    some,
    uniq,
    filter as _filter,
    concat,
} from 'lodash';
import Alert from 'common/components/Alert';
import findIndex from 'lodash/findIndex';

import { responseError } from 'common/utils/Utils';
import * as logger from 'common/utils/Logger';
import * as ApiService from 'common/services/ApiService';
import * as ContractorService from 'common/services/ContractorService';
import * as DocumentService from 'common/services/DocumentService';
import * as WorkOrderService from 'common/services/WorkOrderService';
import { WorkOrderSearchService } from 'common/services/SearchService';
import * as GoogleMapsService from 'common/services/GoogleMapsService';
import * as AdminDistributionCenterService from 'admin/services/AdminDistributionCentersService';
import * as FinancialService from 'common/services/FinancialService';
import * as AdminClientsService from 'admin/services/AdminClientsService';
import { getCompanyAddressById } from 'admin/services/AdminContractorsService';
import {
    isMotiliAccount,
    isContractorAccount,
    isMotiliFinanceDept,
    isMotiliAdmin,
    isMotiliApFinanceDept,
    isMotiliOwner,
} from 'common/services/AuthService';
import { getDefinedIcon } from 'common/utils/google-maps-utils';
import { isValidPropertyStatus } from 'common/utils/property/property-validator';
import { getFinanceUpdateLineItems } from 'common/utils/line-item-utils';
import { getSiteInspectionRequirements } from 'common/utils/work-order-utils';
import { fetchWorkOrderIssueTracking } from 'common/services/WorkOrderService';
import { createContainsLoopbackFilter } from 'common/components/data-grid/DataGridUtils';
import {
    DOCUMENT_TYPE,
    WORK_ORDER_TYPE,
    WORK_ORDER_STATUS,
    WORK_ORDER_ISSUE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';

const baseAcceptableWorkOrderStatuses = [
    WORK_ORDER_STATUS.CREATED,
    WORK_ORDER_STATUS.SCHEDULING,
    WORK_ORDER_STATUS.CONTACT_REQUIRED,
    WORK_ORDER_STATUS.DISPATCHING,
    WORK_ORDER_STATUS.DISPATCHED,
    WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND,
    WORK_ORDER_STATUS.DELAYED,
    WORK_ORDER_STATUS.SCHEDULED,
    WORK_ORDER_STATUS.ORDERED,
    WORK_ORDER_STATUS.DRAFTED,
];

export function revertChecklist(checklist, reason) {
    return WorkOrderService.revertChecklist(checklist, reason);
}

export function getCancelReasons() {
    return ApiService.get(true, 'WorkOrders/cancelReasons');
}

export function getWorkOrders(filterQuery) {
    const filterFields = {
        filter: {
            include: [
                {
                    relation: 'property',
                    scope: {
                        fields: ['stdAddress', 'stdDeliveryLine', 'timezone'],
                    },
                },
                {
                    relation: 'jobManager',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
                {
                    relation: 'jobCoOwner',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
                {
                    relation: 'jobApprovalOwner',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
                {
                    relation: 'contractor',
                    scope: {
                        fields: ['name', 'phone'],
                    },
                },
                {
                    relation: 'contractorTeamMember',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
            ],
            fields: [
                'id',
                'number',
                'documentTypeId',
                'documentSubTypeId',
                'tradeTypeId',
                'priorityId',
                'statusId',
                'extReferenceNumber1',
                'extReferenceNumber2',
                'extReferenceNumber3',
                'extReferenceNumber4',
                'extReferenceNumber5',
                'propertyId',
                'clientId',
                'client',
                'contractorId',
                'contractorTeamMemberId',
                'userId',
                'documentOrigination',
                'created',
                'updated',
                'opsJobManager',
                'coOwner',
                'approvalOwnerId',
                'tradeSubTypeId',
                'fulfillmentDetails',
                'sla',
                'salesRepFullName',
                'jobId',
                'intPoNumber',
                'extPoNumber',
                'schedule',
                'issueId',
                'closedOn',
                'projectId',
            ],
        },
    };
    const query = merge({}, filterFields, { filter: filterQuery });
    return WorkOrderSearchService.find(query);
}

export async function getWorkOrderDetails(id) {
    try {
        const workOrder = await WorkOrderService.fetchWorkOrderByFilterId(
            id,
            'DETAILS'
        );
        const requirements = await getSiteInspectionRequirements(
            id,
            workOrder.requirements
        );
        return {
            ...workOrder,
            requirements,
        };
    } catch (errObject) {
        logger.error(errObject);
        Alert.error(
            `An Unknown Error Occurred; Unable to Retrieve Work Order ${id} : ${responseError(
                errObject
            )}`
        );
        throw errObject;
    }
}

export async function getWorkOrderLineItems(id) {
    try {
        return await WorkOrderService.fetchWorkOrderByFilterId(
            id,
            'LINE_ITEMS'
        );
    } catch (errObject) {
        logger.error(errObject);
        Alert.error(
            `An Unknown Error Occurred; Unable to Retrieve Work Order ${id} : ${responseError(
                errObject
            )}`
        );
        throw errObject;
    }
}

/**
 * @param {number} id - work order id
 * @returns {Promise<Array<import('common/types').Asset>>}
 */
export async function getJobAssetsAndTags(id) {
    return WorkOrderService.getJobAssetsAndTags(id);
}

export async function getPropertyAssetsAndTags(id) {
    return WorkOrderService.getPropertyAssetsAndTags(id);
}

export async function getWorkOrderEvents(id) {
    return WorkOrderService.fetchWorkOrderEvents(id);
}

export async function getWorkOrderRequirements(id) {
    const query = {
        filter: {
            fields: ['id'],
            include: [
                {
                    relation: 'requirements',
                },
            ],
        },
    };

    try {
        return await WorkOrderService.fetchWorkOrder(id, query);
    } catch (errObject) {
        logger.error(errObject);
        Alert.error(
            `An Unknown Error Occurred; Unable to Retrieve Work Order ${id} : ${responseError(
                errObject
            )}`
        );
        throw errObject;
    }
}

export async function getWorkOrderFiles(id) {
    const query = {
        filter: {
            fields: ['id', 'folderId'],
            include: [
                {
                    relation: 'folder',
                    scope: {
                        fields: ['id', 'folderAttachments'],
                        include: {
                            relation: 'folderAttachments',
                            scope: {
                                fields: [
                                    'id',
                                    'folderId',
                                    'fileName',
                                    'mimeType',
                                    'url',
                                    'secureUrl',
                                    'folderAttachmentTypeId',
                                    'createdAt',
                                    'uploadedById',
                                    'uploadedByCompanyId',
                                    'data',
                                ],
                                include: [
                                    {
                                        relation: 'uploadedBy',
                                        scope: {
                                            fields: [
                                                'id',
                                                'firstName',
                                                'lastName',
                                            ],
                                        },
                                    },
                                ],
                            },
                        },
                    },
                },
            ],
        },
    };

    try {
        return await WorkOrderService.fetchWorkOrder(id, query);
    } catch (errObject) {
        logger.error(errObject);
        Alert.error(
            `An Unknown Error Occurred; Unable to Retrieve Work Order ${id} : ${responseError(
                errObject
            )}`
        );
        throw errObject;
    }
}

export async function getWorkOrderHistory(id) {
    try {
        return await WorkOrderService.fetchWorkOrderByFilterId(id, 'HISTORY');
    } catch (errObject) {
        logger.error(errObject);
        Alert.error(
            `An Unknown Error Occurred; Unable to Retrieve Work Order ${id} : ${responseError(
                errObject
            )}`
        );
        throw errObject;
    }
}

/**
 * @param {number} id - work order id
 * @returns {Promise<import('common/types').WorkOrder>}
 */
export async function getWorkOrder(id) {
    let workOrder;
    try {
        workOrder = await WorkOrderService.fetchWorkOrderByFilterId(id, 'BASE');
    } catch (errObject) {
        logger.error(errObject);
        Alert.error(
            `An Unknown Error Occurred; Unable to Retrieve Work Order ${id} : ${responseError(
                errObject
            )}`
        );
        throw errObject;
    }

    const relatedDataPromises = [];

    /**
     * vendor distribution center
     */
    if (
        workOrder.fulfillmentDetails &&
        workOrder.fulfillmentDetails.distributionCenterId
    ) {
        const distributionCenterQuery = {
            filter: {
                fields: [
                    'vendorBranchNumber',
                    'vendorBranchName',
                    'stdAddress',
                    'stdDeliveryLine',
                    'stdCity',
                    'stdPostalCode',
                    'stdRegion',
                ],
            },
        };
        const distributionCenter =
            await AdminDistributionCenterService.getDistributionCenter(
                workOrder.fulfillmentDetails.distributionCenterId,
                distributionCenterQuery
            ).catch(err => {
                logger.error(
                    new Error(
                        `An error occurred fetching work order # ${workOrder.number}. Failed to retrieve distribution center id ${workOrder.fulfillmentDetails.distributionCenterId}`
                    )
                );
                Alert.error(
                    `Unable to retrieve distribution center information for work order #${
                        workOrder.number
                    }. Please contact Motili : ${responseError(err)}`
                );
                return Promise.resolve(null);
            });
        relatedDataPromises.push(distributionCenter);
    } else {
        relatedDataPromises.push(Promise.resolve(null));
    }

    /**
     * next document info
     */
    // if (workOrder.sequenceElement && workOrder.sequenceElement.nextDocumentTypeId && workOrder.sequenceElement.nextDocumentId) {
    //     const nextDocumentQuery = {
    //         filter: {
    //             fields: ['id', 'documentTypeId', 'workOrderTypeId', 'quoteTypeId', 'orderTypeId', 'jobId', 'statusId']
    //         }
    //     };
    //     let p;
    //     switch (workOrder.sequenceElement.nextDocumentTypeId) {
    //     case 'WORK_ORDER': {
    //         p = WorkOrderService.fetchWorkOrder(workOrder.sequenceElement.nextDocumentId, nextDocumentQuery);
    //         break;
    //     }
    //     case 'QUOTE': {
    //         p = QuoteService.findById(workOrder.sequenceElement.nextDocumentId, nextDocumentQuery);
    //         break;
    //     }
    //     case 'ORDER': {
    //         p = OrderService.findById(workOrder.sequenceElement.nextDocumentId, nextDocumentQuery);
    //         break;
    //     }
    //     default: {
    //         p = Promise.resolve(null);
    //     }
    //     }
    //     p.catch((err) => {
    //         logger.error(new Error(`An error occurred fetching work order # ${workOrder.number}. Failed to retrieve next document in job.`));
    //         Alert.error(`Work order #${workOrder.number} is not the active document in job #${workOrder.job.number} but we failed to retrieve the next document. Please contact Motili`);
    //         return Promise.resolve(null);
    //     });
    //     relatedDataPromises.push(p);
    // }

    /**
     * resolve related data
     */
    const [
        distributionCenter,
        // nextDocument
    ] = await Promise.all(relatedDataPromises);

    const requirements = await getSiteInspectionRequirements(
        id,
        workOrder.requirements
    );

    /**
     * assign related data to work order
     */
    workOrder = {
        ...workOrder,
        vendorDistributionCenter: distributionCenter,
        requirements,
        sequenceElement: {
            ...workOrder.sequenceElement,
            // nextDocument
        },
    };

    /**
     * resolve work order
     */
    return workOrder;
}

export function getWorkOrderNumber(id) {
    const query = {
        filter: {
            fields: ['number'],
        },
    };
    return WorkOrderService.fetchWorkOrder(id, query);
}

export async function getWorkOrderSnapshot(id, fetchDispatchingData) {
    let response;
    try {
        response = await WorkOrderService.fetchWorkOrderSnapshot(
            id,
            fetchDispatchingData
        );
    } catch (errObject) {
        logger.error(errObject);
        Alert.error(
            `An Unknown Error Occurred; Unable to Retrieve Work Order Snapshot : ${responseError(
                errObject
            )}`
        );
        throw errObject;
    }
    return response;
}

export async function getWorkOrderDispatchEligibility(id) {
    try {
        return await WorkOrderService.fetchWorkOrderDispatchEligibility(id);
    } catch (errObject) {
        logger.warn(
            `Dispatch eligibility report unavailable for WorkOrder ${id}`,
            errObject
        );
        return undefined;
    }
}

/**
 * gets work order and snapshot by work order id. Returns a promise that resolves to an array. First item is work order, second item is snapshot. Snapshot may be null.
 * @param {Number} id - work order id
 * @returns {Promise<Object>}
 */
export async function getWorkOrderAndSnapshot(id) {
    const response = [];
    try {
        response.push(await getWorkOrder(id));
        response.push(await getWorkOrderSnapshot(id));
    } catch (errObject) {
        const errResponse = responseError(errObject);
        logger.error(errResponse);
        Alert.error(errResponse);
        throw errObject;
    }
    return response;
}

function updateNcfContractors(ncfContractors, targetedContractors) {
    const updatedNcfContractors = [];
    if (ncfContractors && ncfContractors.length > 0) {
        ncfContractors.forEach(ncfContractor => {
            const index = findIndex(targetedContractors, {
                id: ncfContractor.contractorId,
            });
            if (index === -1) {
                // Exclude targetedContractors from ncfContractors
                if (
                    ncfContractor.teamMembers &&
                    ncfContractor.teamMembers.length > 0
                ) {
                    ncfContractor.infoWindow = {
                        content: `
                            <div style="font-weight: bold; cursor: pointer;"><a href="#/ops/contractors/${ncfContractor.contractorId}">${ncfContractor.name}</a></div>
                            <div style="font-weight: bold;">Team Members:</div>
                                <ul>
                        `,
                    };
                    ncfContractor.teamMembers.forEach(teamMember => {
                        ncfContractor.infoWindow.content += `
                            <li><div><span style="font-weight: bold; cursor: pointer;">Name:</span> <a href="#/ops/contractors/${ncfContractor.contractorId}/team">${teamMember.firstName} ${teamMember.lastName}</a></div></li>
                        `;
                    });
                    ncfContractor.infoWindow.content += `
                        </ul>
                    `;
                }
                ncfContractor.icon = getDefinedIcon('ncfNearContractor');
                ncfContractor.isNcfContractor = true;
                updatedNcfContractors.push(ncfContractor);
            }
        });
    }
    return updatedNcfContractors;
}

/**
 * if workOrder is NCF, then get list of contractors within a certain radius (default is 100)
 * of the work orders lat/lng, exclude those that are already in:
 * status.dispatching.details.targetedContractors array
 * @param {object} workOrder
 * @param {object} snapShot
 * @return {object} workOrder
 * @return {object} snapShot
 */
export function getNcfContractorsNearWorkOrderLatLong([workOrder, snapshot]) {
    let ncfContractors = [];
    if (
        workOrder &&
        workOrder.statusId === WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND
    ) {
        const query = {
            latitude: workOrder.property.latitude,
            longitude: workOrder.property.longitude,
            clientId: workOrder.clientId,
        };
        return new Promise((resolve, reject) => {
            ContractorService.getContractorsNearLatLong(query)
                .then(contractors => {
                    // Make sure contractors have the skill (tradeTypeId) required for the work order
                    // and a matching tradeSubTypeId
                    const workOrderTradeTypeId = workOrder.tradeTypeId;
                    const tradeSubTypeId = workOrder.tradeSubTypeId;
                    if (
                        workOrderTradeTypeId &&
                        contractors &&
                        contractors.length > 0
                    ) {
                        contractors = contractors
                            .filter(contractor => {
                                const tradeTypes = contractor.tradeTypes;
                                if (
                                    findIndex(tradeTypes, {
                                        tradeTypeId: workOrderTradeTypeId,
                                        tradeSubTypeId,
                                    }) > -1
                                ) {
                                    return true;
                                }
                                return false;
                            })
                            .filter(contractor => contractor.dispatchable);
                    }
                    ncfContractors = updateNcfContractors(
                        contractors,
                        snapshot.dispatching.details.targetedContractors
                    );
                    return resolve([workOrder, snapshot, ncfContractors]);
                })
                .catch(error => {
                    logger.error(error);
                    Alert.error(
                        `An Unknown Error Occurred; Unable to Contractors near Work Order ${workOrder.id}`
                    );
                    return reject(error);
                });
        });
    }
    return [workOrder, snapshot, ncfContractors];
}

export function getCountOfWorkOrders(query) {
    if (query.where)
        return WorkOrderSearchService.count({ where: query.where });
    return WorkOrderService.count();
}

export function getBilling(req) {
    return ApiService.post(true, 'WorkOrders/filter', req);
}

export function findAvailableJobs(data) {
    return ApiService.get(true, 'WorkOrders/motiliAvailableJobs', data);
}

export function getWorkOrderTypes() {
    return ApiService.get(true, 'WorkOrders/types').then(res =>
        res.map(item => ({ display: item.display, term: item.id }))
    );
}

export async function getDefaultLineItemsById(workOrderTypeId) {
    return ApiService.get(
        true,
        `WorkOrders/${workOrderTypeId}/defaultLineItems`
    ).catch(error => {
        logger.error(error);
        Alert.error(
            `An Unknown Error Occurred; Unable to Retrieve Default Line Items: ${responseError(
                error
            )}`
        );
        throw error;
    });
}

export function getWorkOrderStatuses() {
    return ApiService.get(true, 'WorkOrders/statuses').then(res =>
        res.map(item => ({ display: item.display, term: item.id }))
    );
}

export function getWorkOrderMobileActions(workOrderId) {
    return ApiService.MOSSGet(true, 'ActionSyncs', {
        filter: {
            where: {
                and: [
                    { documentTypeId: DOCUMENT_TYPE.WORK_ORDER },
                    { documentId: workOrderId },
                ],
            },
        },
    });
}

export function createNote(id, data, addToComment, editable = true) {
    const _note = { ...data };
    return WorkOrderService.createNote(id, _note, addToComment, editable);
}

export function updateNote(id, note, mentionsData) {
    const _note = {
        message: note.message,
        pinned: note.pinned,
        mentionsData,
    };
    return WorkOrderService.updateNote(id, note.id, _note);
}

export function createComment(id, comment, mentionsData, issueTrackingId) {
    const _comment = {
        message: comment,
        messageType: 'MOTILI',
        mentionsData,
        issueTrackingId,
    };
    return WorkOrderService.createComment(id, _comment);
}

export function updateComment(id, comment, mentionsData) {
    const _comment = {
        message: comment.message,
        mentionsData,
    };
    return WorkOrderService.updateComment(id, comment.id, _comment);
}

/**
 * update billing of the work order
 * @param {number} workOrderId
 * @param {number} billingId
 * @param {object} billingData
 * @returns {Promise<Object>}
 */
export function billed(workOrderId, billingId, billingData) {
    return ApiService.post(
        true,
        `WorkOrders/${workOrderId}/billed/`,
        billingData,
        {
            billingId,
        }
    );
}

/**
 * update payment of the work order
 * @param {number} workOrderId
 * @param {number} paymentId
 * @param {object} paymentData
 * @returns {Promise<Object>}
 */
export function paid(workOrderId, paymentId, paymentData) {
    return ApiService.post(
        true,
        `WorkOrders/${workOrderId}/paid/`,
        paymentData,
        {
            paymentId,
        }
    );
}

export function syncPayment(paymentId) {
    return ApiService.get(true, `WorkOrders/syncPayment/${paymentId}`);
}

export function syncBilling(billingId) {
    return ApiService.get(true, `WorkOrders/syncBilling/${billingId}`);
}

export function forceSync(workOrderId) {
    return ApiService.get(true, `WorkOrders/${workOrderId}/forceSync`);
}
/**
 * retrieves all billings and payments of the work order
 * @param {number} id
 * @returns {Promise<Array>}
 */
export function fetchFinances(id) {
    return ApiService.get(true, `WorkOrders/${id}/finances`);
}

/**
 * retrieves all auto invoice history of the work order
 * @param {array} invoiceNumbers
 * @returns {Promise<Array>}
 */
export function fetchAutoInvoiceHistory(invoiceNumbers) {
    return ApiService.post(
        true,
        'WorkOrders/finances/autoInvoiceHistory',
        invoiceNumbers
    );
}

/**
 * trigger auto invoice
 * @param {number} clientId
 * @param {number} autoInvoiceNotificationHistoryId
 * @returns {Promise<Object>}
 */
export function triggerAutoInvoice(clientId, historyId) {
    return ApiService.get(
        true,
        `WorkOrders/finances/${clientId}/triggerAutoInvoice/${historyId}`
    );
}

/**
 * @param {Number} id
 * @param {String} typeId
 * @param {String} [description]
 * @param {String} url
 * @param {Object} file
 * @param {String} file.name
 * @param {String} file.type
 * @param {Number} file.size
 * @param {Object} result
 */
export function createPhoto(id, requirement) {
    return WorkOrderService.createPhoto(id, requirement);
}

export function reUploadRejectedWorkOrderRequirement(id, fk, requirement) {
    return WorkOrderService.reUploadRejectedWorkOrderRequirement(
        id,
        fk,
        requirement
    );
}

export function deletePhoto(id, photoId) {
    return WorkOrderService.deletePhoto(id, photoId);
}

export function editUploadedPhotoRequirement(id, photoId, updateData) {
    return WorkOrderService.editUploadedPhotoRequirement(
        id,
        photoId,
        updateData
    );
}

export function getPhotoRequirementApprovalHistory(id, requirementId) {
    return WorkOrderService.getPhotoRequirementApprovalHistory(
        id,
        requirementId
    );
}

export function saveApprovalRequirement(id, requirementId, approval) {
    return WorkOrderService.saveApprovalRequirement(
        id,
        requirementId,
        approval
    );
}
export function bulkApprovalRequirement(id, requirementIds, approval) {
    return WorkOrderService.bulkApprovalRequirement(
        id,
        requirementIds,
        approval
    );
}

export function savePhotoAttributes(id, photoId, updateData) {
    return WorkOrderService.savePhotoAttributes(id, photoId, updateData);
}
/**
 * completes a checklist
 * @param {Number} workOrderId
 * @param {Number} requirementId
 * @param {String} reason
 */
export function completeChecklist(workOrderId, requirementId, reason) {
    return WorkOrderService.completeChecklist(
        workOrderId,
        requirementId,
        reason
    );
}

/**
 * deletes the checklist from the work_order_requirement table and from the clientRequirements column on the work order
 * @param {Number} workOrderId
 * @param {Number} requirementId
 * @param {String} reason
 */
export function removeChecklist(workOrderId, requirementId, reason) {
    return WorkOrderService.removeChecklist(workOrderId, requirementId, reason);
}

export function saveChecklist(checklist, sections) {
    return WorkOrderService.saveChecklist(checklist, sections);
}

/**
 * add checklists to the work_order_requirement table and from the clientRequirements column on the work order
 * @param {Number} workOrderId
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export function addChecklists(workOrderId, data) {
    return WorkOrderService.addChecklists(workOrderId, data);
}

export function removeLineItem(orderId, lineItemId) {
    return WorkOrderService.deleteLineItem(orderId, lineItemId).then(() =>
        getWorkOrder(orderId)
    );
}

export async function bulkRemoveLineItems(workOrderId, lineItemIds) {
    return WorkOrderService.bulkRemoveLineItems(workOrderId, lineItemIds);
}

export async function addLineItems(workOrderId, products) {
    if (!workOrderId || !products || !products.length) {
        return [];
    }
    // products is of form {product, supplied}
    const createLineItems = products.map(p => ({
        sku: p.product.sku,
        suppliedQuantity: p.supplied.quantity,
        suppliedDescription: p.supplied.description,
        suppliedPayout: p.supplied.suppliedPayout,
        suppliedVendorCost: p.supplied.suppliedVendorCost,
        img: p.product.img,
        details: p.product.details,
        equipmentProductSku: p.product.equipmentProductSku,
    }));
    return WorkOrderService.bulkCreateLineItems(workOrderId, createLineItems);
}

/**
 * update a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} lineItem
 * @param {boolean} [finance]
 * @returns {Promise<Object>}
 */
export function updateLineItem(id, lineItemId, lineItem, finance) {
    return WorkOrderService.updateLineItem(id, lineItemId, lineItem, finance);
}

/**
 * adjust a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} adjustment
 * @param {boolean} [finance]
 */
export function adjustFinanceLineItem(id, lineItemId, adjustment, finance) {
    return WorkOrderService.adjustFinanceLineItem(
        id,
        lineItemId,
        adjustment,
        finance
    );
}

export function bulkAdjustLineItems(
    workOrderId,
    bulkAdjustment,
    adjustmentReason,
    adjustmentReasonNote
) {
    return WorkOrderService.bulkAdjustLineItems(
        workOrderId,
        bulkAdjustment,
        adjustmentReason,
        adjustmentReasonNote
    );
}

/**
 * approve a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} reason
 */
export function approveLineItem(id, lineItemId, reason) {
    return WorkOrderService.approveLineItem(id, lineItemId, reason);
}

/**
 * unapprove a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} reason
 */
export function unapproveLineItem(id, lineItemId, reason) {
    return WorkOrderService.unapproveLineItem(id, lineItemId, reason);
}

export function substituteLineItem(id, lineItemId, newLineItem, isCustom) {
    return WorkOrderService.substituteLineItem(
        id,
        lineItemId,
        newLineItem,
        isCustom
    );
}

export function patch(id, _patch) {
    return WorkOrderService.patch(id, _patch);
}

export function fetchWorkOrderNumberSuggestions(workOrderNumberSearchValue) {
    const query = {
        filter: {
            where: {
                number: { like: `%${workOrderNumberSearchValue}%` },
            },
            fields: ['id', 'number'],
        },
    };
    return WorkOrderService.fetchWorkOrders(query);
}

/**
 * @todo move to AdminQuoteService
 * @param {*} workOrderId
 */
export function fetchWorkOrderForDiagnosticQuote(workOrderId) {
    const query = {
        filter: {
            fields: [
                'id',
                'number',
                'tradeTypeId',
                'tradeSubTypeId',
                'workOrderTypeId',
                'priorityId',
                'clientId',
                'propertyId',
                'contractorId',
                'contractorTeamMemberId',
                'contact',
                'occupied',
                'jobId',
                'statusId',
                'opsJobManager',
                'coOwner',
                'issueId',
            ],
            include: [
                {
                    relation: 'property',
                    scope: {
                        fields: [
                            'id',
                            'stdDeliveryLine',
                            'stdLastLine',
                            'rawAddress',
                            'latitude',
                            'longitude',
                            'typeId',
                            'contact',
                            'propertyProfileId',
                            'clientId',
                            'timezone',
                            'statusId',
                        ],
                        include: [
                            {
                                relation: 'activePropertyProfile',
                                scope: {
                                    include: [
                                        {
                                            relation:
                                                'primaryPreferredContractor',
                                        },
                                        {
                                            relation:
                                                'secondaryPreferredContractor',
                                        },
                                        {
                                            relation: 'preferredContractor3',
                                        },
                                        {
                                            relation: 'preferredContractor4',
                                        },
                                    ],
                                },
                            },
                            {
                                relation: 'client',
                                scope: {
                                    field: [
                                        'id',
                                        'name',
                                        'doingBusinessAs',
                                        'statusId',
                                        'operationInCountry',
                                    ],
                                    include: [
                                        {
                                            relation: 'requirements',
                                            scope: {
                                                where: {
                                                    requirementTypeId: {
                                                        inq: [
                                                            'CLIP_LEVEL',
                                                            'PHOTO',
                                                            'DEFAULT',
                                                            'CHECKLIST',
                                                            'ASSET_TAG',
                                                            'VIDEO',
                                                        ],
                                                    },
                                                },
                                            },
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                },
                {
                    relation: 'client',
                    scope: {
                        fields: ['id', 'name', 'doingBusinessAs'],
                    },
                },
                {
                    relation: 'contractor',
                    scope: {
                        fields: [
                            'id',
                            'name',
                            'doingBusinessAs',
                            'phone',
                            'contact',
                        ],
                    },
                },
                {
                    relation: 'contractorTeamMember',
                    scope: {
                        fields: ['id', 'firstName', 'lastName'],
                        include: [
                            {
                                relation: 'preferences',
                            },
                        ],
                    },
                },
                'contacts',
                {
                    relation: 'job',
                    scope: {
                        include: [
                            {
                                relation: 'project',
                                scope: {
                                    fields: ['name'],
                                },
                            },
                        ],
                    },
                },
            ],
        },
    };
    return WorkOrderService.fetchWorkOrderWithPreferredContractor(
        workOrderId,
        query
    );
}

export function getDelayReasonTypes() {
    return WorkOrderService.getDelayReasonTypes();
}

export function getUnassignReasonTypes() {
    return WorkOrderService.getUnassignReasonTypes();
}

export function getRescheduleReasonTypes() {
    return WorkOrderService.getRescheduleReasonTypes();
}

export function getCreateQuoteFromWorkOrderReasonTypes() {
    return WorkOrderService.getCreateQuoteFromWorkOrderReasonTypes();
}

export function getWaitingForApprovalReasonTypes() {
    return WorkOrderService.getWaitingForApprovalReasonTypes();
}

export function fetchWorkOrderForDiagnosticWorkOrder(workOrderId) {
    const query = {
        filter: {
            fields: [
                'id',
                'number',
                'tradeTypeId',
                'tradeSubTypeId',
                'workOrderTypeId',
                'priorityId',
                'clientId',
                'propertyId',
                'contractorId',
                'contractorTeamMemberId',
                'contact',
                'opsJobManager',
                'coOwner',
                'clipLevel',
                'extReferenceNumber1',
                'extReferenceNumber2',
                'extReferenceNumber3',
                'extReferenceNumber4',
                'extReferenceNumber5',
                'occupied',
                'instructions',
                'documentTypeId',
                'contact',
                'jobId',
                'cleanAndCheck',
                'issueId',
            ],
            include: [
                {
                    relation: 'property',
                    scope: {
                        fields: [
                            'id',
                            'stdDeliveryLine',
                            'stdLastLine',
                            'rawAddress',
                            'latitude',
                            'longitude',
                            'typeId',
                            'contact',
                            'clientId',
                            'propertyProfileId',
                            'timezone',
                            'statusId',
                        ],
                        include: [
                            {
                                relation: 'client',
                                scope: {
                                    fields: [
                                        'id',
                                        'name',
                                        'doingBusinessAs',
                                        'statusId',
                                        'agentBilling',
                                        'operationInCountry',
                                        'enabledVideoRecording',
                                        'enabledAutoDispatch',
                                        'autoDispatchResumeDate',
                                    ],
                                    include: [
                                        {
                                            relation: 'settings',
                                        },
                                        {
                                            relation: 'requirements',
                                            scope: {
                                                where: {
                                                    requirementTypeId: {
                                                        inq: [
                                                            'CLIP_LEVEL',
                                                            'PHOTO',
                                                            'DEFAULT',
                                                            'CHECKLIST',
                                                            'ASSET_TAG',
                                                            'VIDEO',
                                                        ],
                                                    },
                                                },
                                            },
                                        },
                                    ],
                                },
                            },
                            {
                                relation: 'activePropertyProfile',
                                scope: {
                                    include: [
                                        {
                                            relation:
                                                'primaryPreferredContractor',
                                        },
                                        {
                                            relation:
                                                'secondaryPreferredContractor',
                                        },
                                        {
                                            relation: 'preferredContractor3',
                                        },
                                        {
                                            relation: 'preferredContractor4',
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                },
                {
                    relation: 'client',
                    scope: {
                        fields: ['id', 'name', 'doingBusinessAs'],
                        include: [
                            {
                                relation: 'requirements',
                                scope: {
                                    where: {
                                        requirementTypeId: {
                                            inq: [
                                                'CLIP_LEVEL',
                                                'PHOTO',
                                                'DEFAULT',
                                                'CHECKLIST',
                                                'ASSET_TAG',
                                                'VIDEO',
                                            ],
                                        },
                                    },
                                },
                            },
                        ],
                    },
                },
                {
                    relation: 'contractor',
                    scope: {
                        fields: [
                            'id',
                            'name',
                            'doingBusinessAs',
                            'phone',
                            'contact',
                        ],
                    },
                },
                {
                    relation: 'contractorTeamMember',
                    scope: {
                        fields: ['id', 'firstName', 'lastName'],
                        include: [
                            {
                                relation: 'preferences',
                            },
                        ],
                    },
                },
                {
                    relation: 'jobManager',
                    scope: {
                        fields: ['id', 'firstName', 'lastName'],
                    },
                },
                {
                    relation: 'contacts',
                },
                {
                    relation: 'job',
                    scope: {
                        include: [
                            {
                                relation: 'project',
                                scope: {
                                    fields: ['name'],
                                },
                            },
                        ],
                    },
                },
            ],
        },
    };
    return WorkOrderService.fetchWorkOrderWithPreferredContractor(
        workOrderId,
        query
    );
}

/*
 * -=-=-=-=-=-=-=-=-= ACTION api calls =-=-=-=-=-=-=-=--=-=-=-
 */

/**
 * work order fulfilled action
 * @param {Number} id
 * @returns {Promise<Object>}
 */
export function fulfilled(id) {
    return WorkOrderService.fulfilled(id);
}

/**
 *
 * @param {string} id
 * @param {Object} _schedule
 * @param {string} _schedule.startDateTime
 * @param {string} _schedule.endDateTime
 * @param {string} _schedule.type
 * @param {string} _schedule.eventId
 * @param {string} [reason]
 * @param {string} [reasonNote]
 * @param {string} [followupActionId]
 * @param {number} [assignContractorId]
 * @param {number} [assignContractorTeamMemberId]
 * @param {array} [workers]
 * @returns {Promise<Object>}
 */
export function schedule(
    id,
    _schedule,
    reason,
    reasonNote,
    followupActionId,
    assignContractorId,
    assignContractorTeamMemberId,
    workers = undefined
) {
    return WorkOrderService.schedule(
        id,
        _schedule,
        reason,
        reasonNote,
        followupActionId,
        assignContractorId,
        assignContractorTeamMemberId,
        workers
    );
}

export function reschedule(
    id,
    startDateTime,
    reasonType,
    reasonComments,
    isNCF
) {
    return WorkOrderService.reschedule(
        id,
        startDateTime,
        reasonType,
        reasonComments,
        isNCF
    );
}

/**
 *
 * @param {*} id
 * @param {*} contractorId
 * @param {*} contractorTeamMemberId
 * @param {*} reason
 * @param {*} reasonNote
 * @param {array} [workers]
 * @returns {Promise<Object>}
 */
export function assign(
    id,
    contractorId,
    contractorTeamMemberId,
    reason,
    reasonNote,
    workers = undefined,
    continueAssigned = undefined
) {
    return WorkOrderService.assign(
        id,
        contractorId,
        contractorTeamMemberId,
        reason,
        reasonNote,
        workers,
        continueAssigned
    );
}

/**
 *
 * @param {*} id
 * @param {*} contractorId
 * @param {*} contractorTeamMemberId
 * @param {*} reason
 * @param {*} reasonNote
 * @param {array} [workers]
 * @returns {Promise<Object>}
 */
export function reAssign(
    id,
    contractorId,
    contractorTeamMemberId,
    reason,
    reasonNote,
    workers = undefined,
    continueAssigned = undefined
) {
    return WorkOrderService.reAssign(
        id,
        contractorId,
        contractorTeamMemberId,
        reason,
        reasonNote,
        workers,
        continueAssigned
    );
}

/**
 *
 * @param {number} id
 * @param {string} quoteTypeId
 * @param {array} reasons
 * @returns {Promise<Object>}
 */
export function complete(id, quoteTypeId, reasons) {
    return WorkOrderService.complete(id, quoteTypeId, reasons);
}

/**
 *
 * @param {*} id
 * @param {*} reason
 * @returns {Promise<Object>}
 */
export function reOpen(id, reason) {
    return WorkOrderService.reOpen(id, reason);
}

/**
 *
 * @param {string} id
 * @param {string} reason
 * @param {string} reasonType
 * @param {string} reasonTypeLabel
 * @param {string} note
 * @param {boolean} removeSchedule
 * @returns {Promise<Object>}
 */
export function unAssign(
    id,
    reason,
    reasonType,
    reasonTypeLabel,
    note,
    removeSchedule
) {
    return WorkOrderService.unAssign(
        id,
        reason,
        reasonType,
        reasonTypeLabel,
        note,
        removeSchedule
    );
}

/**
 *
 * @param {number} id workOrderId
 * @param {object} checkIn
 * @param {string} checkIn.checkInReason
 * @param {string} checkIn.checkedInAt
 * @param {object} checkIn.checkInCoords
 * @param {number} checkIn.checkInCoords.lat
 * @param {number} checkIn.checkInCoords.lng
 * @param {number} checkIn.checkedInUserId only needed for Admin
 * @param {number} checkIn.checkedInUser
 * @param {number} checkIn.timezoneId
 * @returns {Promise<Object>}
 */
export function checkIn(id, checkIn) {
    return WorkOrderService.checkIn(id, checkIn);
}

/**
 *
 * @param {number} id
 * @param {number} checkInId
 * @param {object} checkOut
 * @param {string} checkOut.checkOutReason
 * @param {string} checkOut.checkedOutAt
 * @param {object} checkOut.checkOutCoords
 * @param {number} checkOut.timezoneId
 * @param {number} checkOut.checkOutCoords.lat
 * @param {number} checkOut.checkOutCoords.lng
 * @returns {Promise<Object>}
 */
export function checkOut(id, checkInId, checkOut) {
    return WorkOrderService.checkOut(id, checkInId, checkOut);
}

export function adminNoAccountCheckOut(id, checkOut) {
    return WorkOrderService.adminNoAccountCheckOut(id, checkOut);
}

/**
 *
 * @param {number | string} id
 * @param {object} data
 * @param {string} data.quoteTypeId
 * @param {string} data.onBehalfOfReason
 * @param {array} data.reasons
 * @param {object} data.permitResponses
 * @param {date} data.installDate
 * @param {boolean} data.createRepairWO
 * @returns {Promise<Object>}
 */
export function submit(id, data) {
    return WorkOrderService.submit(id, { ...data, doneOnBehalfOf: true });
}

/**
 *
 * @param {number | string} id
 * @param {object} data
 * @param {object} data.lhs
 * @returns {Promise<Object>}
 */
export function submitLHSRisk(id, data) {
    return WorkOrderService.identifyLHSRisk(id, {
        ...data,
        doneOnBehalfOf: true,
    });
}

/**
 * CS-3995: Potential Damage exception flag is Motili-internal only and available in all statuses.
 * @param {Object} workOrder
 * @returns {Boolean}
 */
export function shouldDisplayActionPotentialDamage(workOrder) {
    if (!workOrder) {
        return false;
    }
    // Hide once an active Potential Damage flag exists — manage it via the exceptions widget
    // (edit / remove); only one active flag at a time.
    return !(workOrder.issueId || []).includes(
        WORK_ORDER_ISSUE.POTENTIAL_DAMAGE
    );
}

/**
 * @param {number} id work order id
 * @param {Object} potentialDamage { riskType, riskReasons, otherReason, assigneeId, notes, attachments }
 */
export function identifyPotentialDamage(id, potentialDamage) {
    return WorkOrderService.identifyPotentialDamage(id, { potentialDamage });
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @param {string} reasonType
 * @param {string} endDateTime
 * @returns {Promise<Object>}
 */
export function delay(id, reason, reasonType, delayEndDateTime) {
    return WorkOrderService.delay(id, reason, reasonType, delayEndDateTime);
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @param {string} reasonType
 * @param {string} endDateTime
 * @returns {Promise<Object>}
 */
export function createQuote(id, reason, reasonType, quoteTypeId) {
    return WorkOrderService.createQuote(id, reason, reasonType, quoteTypeId);
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function cancel(id, reason, reasonId) {
    return WorkOrderService.cancel(id, reason, reasonId);
}

export function changePriority(
    orderId,
    priorityId,
    reasonId,
    reasonIdLabel,
    reason
) {
    return WorkOrderService.changePriority(
        orderId,
        priorityId,
        reasonId,
        reasonIdLabel,
        reason
    );
}

/**
 *
 * @param {number} id
 * @param {string} ownerId
 * @returns {Promise<Object>}
 */
export function transferOwner(id, ownerId, coOwnerId, approvalOwnerId) {
    return WorkOrderService.transferOwner(
        id,
        ownerId,
        coOwnerId,
        approvalOwnerId
    );
}

export function close(id) {
    return WorkOrderService.close(id);
}

/**
 *
 * @param {array} workOrderIds
 * @param {object} data
 * @returns {Promise<Object>}
 */
export function bulkEdit(data) {
    return WorkOrderService.bulkEdit(data);
}

export async function processFilters(
    searchFilters,
    advancedFilters,
    activeQuery,
    userId
) {
    const query = merge({}, activeQuery, {
        where: {
            and: [],
        },
    });

    if (
        !Object.keys(searchFilters).length &&
        !Object.keys(advancedFilters).length
    ) {
        return query;
    }

    // Search filter section
    const filterGroups = {};

    const containsFilterMap = {
        CLIENT: 'client',
        EXT_REFERENCE_NUMBER_1: 'extReferenceNumber1',
        EXT_REFERENCE_NUMBER_2: 'extReferenceNumber2',
        EXT_REFERENCE_NUMBER_3: 'extReferenceNumber3',
        EXT_REFERENCE_NUMBER_4: 'extReferenceNumber4',
        EXT_REFERENCE_NUMBER_5: 'extReferenceNumber5',
        NUMBER: 'sortByNumber',
        PO_NUMBER: 'extPoNumber',
    };

    const inqFilterMap = {
        ADDRESS: 'propertyId',
        APPROVAL_OWNER: 'approvalOwnerId',
        CLIENT_ID: 'clientId',
        CO_OWNER: 'coOwner',
        CONTRACTOR: 'contractorId',
        CREATED_BY: 'userId',
        JOB_NUMBER: 'jobId',
        OWNER: 'opsJobManager',
        PROJECT: 'jobId',
        TECHNICIAN: 'contractorTeamMemberId',
    };

    Object.keys(searchFilters).forEach(key => {
        if (!filterGroups[key]) {
            filterGroups[key] = { or: [] };
        }

        if (containsFilterMap[key]) {
            filterGroups[key].or.push(
                ...searchFilters[key].map(
                    filter =>
                        createContainsLoopbackFilter(
                            containsFilterMap[key],
                            filter.term
                        ).where
                )
            );
            return;
        }

        if (inqFilterMap[key]) {
            filterGroups[key].or.push({
                [inqFilterMap[key]]: {
                    inq: searchFilters[key].flatMap(filter => filter.term),
                },
            });
        }
    });

    Object.values(filterGroups).forEach(group => {
        if (group.or.length > 0) {
            query.where.and.push(group);
        }
    });

    // Advanced filter section
    const typeFilter = { or: [] };
    const statusFilter = { or: [] };
    const tradeFilter = { or: [] };
    const priorityFilter = { or: [] };
    const segmentsFilter = [];
    const lineBusinessFilter = [];
    const financialStatuses = [];
    const completedFilters = [];
    const issueFilter = { or: [] };
    let salesInvoice = '';
    let purchaseInvoice = '';
    const issueForms = [];
    const issueReasonForms = [];
    const issueSecondaryReasonForms = [];

    Object.keys(advancedFilters).forEach(key => {
        switch (key) {
            case 'excludeClient':
                advancedFilters[key].forEach(filter => {
                    query.where.and.push({
                        clientId: {
                            neq: filter.term,
                        },
                    });
                });
                break;
            case 'segments':
                advancedFilters[key].forEach(filter => {
                    segmentsFilter.push(filter.form);
                });
                break;
            case 'lineBusiness':
                advancedFilters[key].forEach(filter => {
                    lineBusinessFilter.push(filter.form);
                });
                break;
            case 'createdBefore':
                query.where.and.push({
                    created: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'createdAfter':
                query.where.and.push({
                    created: {
                        gt: moment(advancedFilters[key].term).endOf('day'),
                    },
                });
                break;
            case 'closedBefore':
                query.where.and.push({
                    closedOn: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'closedAfter':
                query.where.and.push({
                    closedOn: {
                        gt: moment(advancedFilters[key].term).endOf('day'),
                    },
                });
                break;
            case 'ownerIs':
                if (advancedFilters[key].term === 'Me') {
                    query.where.and.push({ opsJobManager: userId });
                }
                break;
            case 'coOwner':
                if (advancedFilters[key].term === 'Me') {
                    query.where.and.push({ coOwner: userId });
                }
                break;
            case 'approvalOwner':
                if (advancedFilters[key].term === 'Me') {
                    query.where.and.push({ approvalOwnerId: userId });
                }
                break;
            case 'occupied':
                query.where.and.push({
                    occupied: advancedFilters[key].term === 'Occupied',
                });
                break;
            case 'SLAGreater':
                query.where.and.push({
                    sla: { gt: `${advancedFilters[key].term} days` },
                });
                break;
            case 'SLALess':
                query.where.and.push({
                    sla: { lt: `${advancedFilters[key].term} days` },
                });
                break;
            case 'excludeNA':
                if (advancedFilters[key].term) {
                    query.where.and.push({
                        statusId: { nin: ['COMPLETED', 'CLOSED', 'CANCELLED'] },
                    });
                }
                break;
            case 'includeClientId':
                if (advancedFilters[key].term) {
                    query.where.and.push({
                        clientId: advancedFilters[key].term,
                    });
                }
                break;
            case 'ageGreater':
                query.where.and.push({
                    created: {
                        lt: moment()
                            .subtract(advancedFilters[key].term, 'days')
                            .format(),
                    },
                });
                break;
            case 'ageLess':
                query.where.and.push({
                    created: {
                        gt: moment()
                            .subtract(advancedFilters[key].term, 'days')
                            .format(),
                    },
                });
                break;
            case 'type':
                typeFilter.or.push({
                    documentSubTypeId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'trade':
                tradeFilter.or.push({
                    tradeTypeId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'status':
                statusFilter.or.push({
                    statusId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'financialStatus':
                advancedFilters[key].forEach(filter => {
                    financialStatuses.push(filter.term);
                });
                break;
            case 'priority':
                priorityFilter.or.push({
                    priorityId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'created90Days':
                query.where.and.push({
                    created: {
                        gt: moment().subtract(90, 'days'),
                        lt: moment().add(1, 'days'),
                    },
                });
                break;
            case 'completedAfter':
                completedFilters.push({
                    ...advancedFilters[key],
                    target: 'completedAfter',
                });
                break;
            case 'completedBefore':
                completedFilters.push({
                    ...advancedFilters[key],
                    target: 'completedBefore',
                });
                break;
            case 'planned':
                if (advancedFilters[key].term === 'Planned') {
                    query.where.and.push({ projectId: { neq: null } });
                } else {
                    query.where.and.push({ projectId: null });
                }
                break;
            case 'salesInvoiceNumber':
                salesInvoice = advancedFilters[key].term.trim();
                break;
            case 'purchaseInvoiceNumber':
                purchaseInvoice = advancedFilters[key].term.trim();
                break;
            case 'country':
                query.where.and.push({ country: advancedFilters[key].term });
                break;
            case 'issue':
                advancedFilters[key].forEach(filter => {
                    if (filter.term === 'NONE') {
                        issueFilter.or.push(
                            { issueId: null },
                            { issueId: { like: `%[]%` } }
                        );
                    } else {
                        issueFilter.or.push({
                            issueId: { like: `%${filter.term}%` },
                        });
                        issueForms.push(filter.form);
                    }
                });
                break;
            case 'issueReason':
                advancedFilters[key].forEach(filter => {
                    issueReasonForms.push(filter.form);
                });
                break;
            case 'issueSecondaryReason':
                advancedFilters[key].forEach(filter => {
                    issueSecondaryReasonForms.push(filter.form);
                });
                break;
            case 'salesRepId':
                query.where.and.push({
                    salesRepId: { ilike: `%${advancedFilters[key].term}%` },
                });
                break;
            case 'salesRepName':
                query.where.and.push({
                    salesRepFullName: {
                        ilike: `%${advancedFilters[key].term}%`,
                    },
                });
                break;
            default:
        }
    });

    if (completedFilters.length) {
        const completedAfterFilter = completedFilters.find(
            f => f.target === 'completedAfter'
        );
        let completedAfter;
        if (completedAfterFilter) {
            completedAfter = moment(completedAfterFilter.term)
                .endOf('day')
                .toISOString();
        }
        const completedBeforeFilter = completedFilters.find(
            f => f.target === 'completedBefore'
        );
        let completedBefore;
        if (completedBeforeFilter) {
            completedBefore = moment(completedBeforeFilter.term)
                .endOf('day')
                .subtract(1, 'days')
                .toISOString();
        }
        const completedWorkOrderIds =
            await WorkOrderService.fetchCompletedWorkOrders(
                completedAfter,
                completedBefore,
                false
            );
        query.where.and.push({ id: { inq: completedWorkOrderIds } });
    }

    let billingList;
    let purchaseList;
    if (salesInvoice && !purchaseInvoice) {
        billingList = await getWorkOrderIdByBillingStatues(
            financialStatuses,
            salesInvoice
        );
        purchaseList = await getWorkOrderIdByPaymentStatues(
            financialStatuses,
            purchaseInvoice,
            billingList.length > 0 ? billingList : undefined
        );
    } else if (purchaseInvoice && !salesInvoice) {
        purchaseList = await getWorkOrderIdByPaymentStatues(
            financialStatuses,
            purchaseInvoice
        );
        billingList = await getWorkOrderIdByBillingStatues(
            financialStatuses,
            salesInvoice,
            purchaseList.length > 0 ? purchaseList : undefined
        );
    } else {
        [billingList, purchaseList] = await Promise.all([
            getWorkOrderIdByBillingStatues(financialStatuses, salesInvoice),
            getWorkOrderIdByPaymentStatues(financialStatuses, purchaseInvoice),
        ]);
    }

    if (!salesInvoice && !purchaseInvoice && financialStatuses.length) {
        query.where.and.push({
            id: { inq: uniq(concat(billingList, purchaseList)) },
        });
    } else {
        if (salesInvoice || financialStatuses.length) {
            query.where.and.push({ id: { inq: billingList } });
        }
        if (purchaseInvoice || financialStatuses.length) {
            query.where.and.push({ id: { inq: purchaseList } });
        }
    }

    query.where.and.push(
        typeFilter,
        statusFilter,
        tradeFilter,
        priorityFilter,
        issueFilter
    );

    if (segmentsFilter.length || lineBusinessFilter.length) {
        let clientIdArray = [];
        const retailStoreInfo = [];

        const retailStoreInfoFilters = [
            'retailRegion',
            'retailStore',
            'retailStoreNumber',
        ];

        retailStoreInfoFilters.forEach(filter => {
            if (advancedFilters[filter]) {
                retailStoreInfo.push({
                    target: filter,
                    term: advancedFilters[filter].term,
                });
            }
        });

        clientIdArray = union(
            clientIdArray,
            await AdminClientsService.getClientIdBySettingsType(
                segmentsFilter,
                lineBusinessFilter,
                retailStoreInfo
            )
        );
        const clientIdFilter = { clientId: { inq: clientIdArray } };
        query.where.and.push({ or: [clientIdFilter] });
    }

    if (
        issueForms.length &&
        (issueReasonForms.length || issueSecondaryReasonForms.length)
    ) {
        const workOrderIdsFromIssues = await getWorkOrderIdsByIssue(
            issueForms,
            issueReasonForms,
            issueSecondaryReasonForms
        );
        if (workOrderIdsFromIssues.length > 0) {
            query.where.and.push({ id: { inq: workOrderIdsFromIssues } });
        } else {
            query.where.and.push({ id: -1 }); // case not found wo from issue conditions
        }
    }

    return query;
}

async function getWorkOrderIdByBillingStatues(
    statuses,
    invoiceNumber,
    workOrderId
) {
    if (statuses.length > 0 || invoiceNumber) {
        return FinancialService.findWorkOrderBillingByStatuses(
            statuses,
            invoiceNumber,
            workOrderId
        ).then(billings => billings?.map(b => b.workOrderId));
    }
    return [];
}

async function getWorkOrderIdByPaymentStatues(
    statuses,
    invoiceNumber,
    workOrderId
) {
    if (statuses.length > 0 || invoiceNumber) {
        return FinancialService.findWorkOrderPaymentByStatuses(
            statuses,
            invoiceNumber,
            workOrderId
        ).then(payments => payments?.map(p => p.workOrderId));
    }
    return [];
}

export function mapFulfillmentDetailsFromTruck(workOrder, fulfillmentDetails) {
    return WorkOrderService.mapFulfillmentDetailsFromTruck(
        workOrder,
        fulfillmentDetails
    );
}

/**
 * isAssetTaggingRequired
 * @param {Object[]} lineItems
 * @returns {boolean}
 */
export function isAssetTaggingRequired(lineItems) {
    return WorkOrderService.isAssetTaggingRequired(lineItems);
}

/**
 * assetTaggingRequirementsMet
 * @param {Object} workOrder
 * @param {Object[]} assetConfigurations
 * @returns {boolean}
 */
export async function assetTaggingRequirementsMet(
    workOrder,
    assetConfigurations
) {
    return WorkOrderService.assetTaggingRequirementsMet(
        workOrder,
        assetConfigurations
    );
}

/**
 * requirementsMet - returns true if requirements met, otherwise false
 * @param {object[]} requirements
 * @param {object[]} clientRequirements
 * @param {object[]} projectRequirements
 * @returns {boolean}
 */
export function requirementsMet(
    requirements,
    clientRequirements,
    projectRequirements
) {
    return WorkOrderService.requirementsMet(
        requirements,
        clientRequirements,
        projectRequirements
    );
}

export function containRejectedPhoto(requirements) {
    return WorkOrderService.containRejectedPhoto(requirements);
}

/**
 * checkListRequirementsMet - returns true if checkList Requirements met, otherwise false
 * @param {object[]} requirements
 * @param {object[]} clientRequirements
 * @param {object[]} projectRequirements
 * @returns {boolean}
 */
export function checkListRequirementsMet(
    requirements,
    clientRequirements,
    projectRequirements
) {
    return WorkOrderService.checkListRequirementsMet(
        requirements,
        clientRequirements,
        projectRequirements
    );
}

/**
 * returns an array of marker objects for use with GoogleMaps component
 * this code was largely lifted from legacy AdminPortal
 * @param {Object} snapshot
 * @param {Object} snapshot.dispatching
 * @param {Object} snapshot.dispatching.details
 * @param {Object[]} snapshot.dispatching.details.targetedContractors
 * @returns {Promise<Object[]>} markers
 */
export async function getContractorDispatchingMarkersFromSnapshot(snapshot) {
    const ncfDispatchingMarkers = snapshot.contractors || [];
    const targetedContractors = get(
        snapshot,
        'dispatching.details.targetedContractors',
        []
    );
    const notAllTeamMembersHaveReplied = contractor => {
        return !!contractor.teamMembers.find(teamMember => !teamMember.replied);
    };
    const dispatchingMarkers = map(targetedContractors, async contractor => {
        let iconLabel = 'contractorNoReply';
        if (contractor.repliedFromContractor || contractor.replied) {
            if (contractor.accepted) {
                iconLabel = 'contractorAccepted';
            } else if (notAllTeamMembersHaveReplied(contractor)) {
                iconLabel = 'contractorSomeDeclined';
            } else {
                iconLabel = 'contractorDeclined';
            }
        }

        const dispatchRule = contractor.contractorDispatchRule.length
            ? contractor.contractorDispatchRule[0]
            : false;
        if (
            dispatchRule &&
            dispatchRule.dispatchingDisabled &&
            moment(dispatchRule.endDate).isAfter(moment())
        ) {
            iconLabel = 'contractorDispatchingDisabled';
        }
        const icon = getDefinedIcon(iconLabel);

        const lastSentNotficationToContractorCompany =
            contractor.lastSentNotification
                ? moment(contractor.lastSentNotification).format(
                      'MM-DD-YYYY h:mm a'
                  )
                : 'N/A';
        let contentString = `<div style="font-weight: bold; cursor: pointer;"><a href="#/ops/contractors/${contractor.id}">${contractor.name}</a></div>
            <div><span style="font-weight: bold">Notices sent:</span> ${contractor.noticesSent}</div>
            <div><span style="font-weight: bold">Last sent:</span> ${lastSentNotficationToContractorCompany}</div>`;

        if (
            dispatchRule &&
            dispatchRule.dispatchingDisabled &&
            moment(dispatchRule.endDate).isAfter(moment())
        ) {
            contentString = `<div style="font-weight: bold; cursor: pointer;"><a href="#/ops/contractors/${
                contractor.id
            }">${contractor.name}</a></div>
            <div><span style="font-weight: bold">Dispatching:</span> Disabled</div>
            <div><span style="font-weight: bold">Reason:</span> ${
                dispatchRule.type === 'PROJECT' ? 'Project Contractor' : ''
            }</div>
            <div><span style="font-weight: bold">Start Date:</span> ${moment(
                dispatchRule.startDate
            ).format('LL')}</div>
            <div><span style="font-weight: bold">End Date:</span> ${moment(
                dispatchRule.endDate
            ).format('LL')}</div>`;
        }

        if (contractor.teamMembers.length) {
            contentString += `<div style="font-weight: bold;">Team Members:</div>
                <ul>`;
            forEach(contractor.teamMembers, teamMember => {
                const replied = teamMember.replied ? 'Yes' : 'No';
                const accepted = teamMember.accepted ? 'Yes' : 'No';
                const lastSentNotificationToTeamMember =
                    teamMember.lastSentNotification
                        ? moment(teamMember.lastSentNotification).format(
                              'MM-DD-YYYY h:mm a'
                          )
                        : 'N/A';
                contentString += `<li><span style="font-weight: bold;">Name: </span><a href="#/ops/contractors/${contractor.id}/team">${teamMember.name}</a>
                    <div><span style="font-weight: bold;">Replied: </span>${replied}</div>`;
                if (teamMember.replied) {
                    contentString += `<div><span style="font-weight: bold;">Accepted: </span>${accepted}</div>`;
                }
                contentString += `<div><span style="font-weight: bold;">Last Notification: </span>${lastSentNotificationToTeamMember}</div></li>`;
            });
            contentString += '</ul>';
        }
        if (contractor.accepted) {
            contentString += `<div><span style="font-weight: bold">Accepted:</span> ${lastSentNotficationToContractorCompany}</div>`;
        } else if (contractor.replied) {
            contentString += `<div><span style="font-weight: bold">Rejected:</span> ${lastSentNotficationToContractorCompany}</div>`;
        }
        if (!contractor.lat || !contractor.lng) {
            const companyAddress = await getCompanyAddressById(
                contractor.addressId
            );
            const geocodedAddress = await GoogleMapsService.geocodeAddress(
                companyAddress.rawAddress
            );
            return {
                position: {
                    lat: geocodedAddress.geometry.location.lat(),
                    lng: geocodedAddress.geometry.location.lng(),
                },
                icon,
                iconLabel,
                infoWindow: {
                    content: contentString,
                },
                contractor,
            };
        }
        return {
            // title: contractor.name,
            position: {
                lat: contractor.lat,
                lng: contractor.lng,
            },
            icon,
            iconLabel,
            infoWindow: {
                content: contentString,
            },
            contractor,
        };
    }).concat(ncfDispatchingMarkers);
    return Promise.all(dispatchingMarkers);
}

/**
 * validates admin diagnostic work order creation form
 * @param {object} workOrder
 * @returns {boolean}
 */
export function diagnosticCreationFormIsValid(workOrder, deprecated = false) {
    if (!WorkOrderService.isValid(workOrder, deprecated)) {
        return false;
    }
    return true;
}

/**
 * validates admin inspection work order creation form
 * @param {object} workOrder
 * @returns {boolean}
 */
export function inspectionCreationFormIsValid(workOrder, deprecated = false) {
    if (!WorkOrderService.isValid(workOrder, deprecated)) return false;
    if (!workOrder.contacts[0].typeId) return false;

    return true;
}

/**
 * validates admin walkthrough work order creation form
 * @param {object} workOrder
 * @returns {boolean}
 */
export function walkthroughCreationFormIsValid(workOrder, deprecated = false) {
    if (!WorkOrderService.isValid(workOrder, deprecated)) {
        return false;
    }
    if (!workOrder.schedule) {
        return false;
    }
    return true;
}

/**
 * validates admin preventative work order creation form
 * @param {object} workOrder
 * @returns {boolean}
 */
export function preventativeCreationFormIsValid(workOrder, deprecated = false) {
    if (!WorkOrderService.isValid(workOrder, deprecated)) {
        return false;
    }
    return true;
}

/**
 * validates admin warranty work order creation form
 * @param {object} workOrder
 * @returns {boolean}
 */
export function warrantyCreationFormIsValid(workOrder) {
    if (!WorkOrderService.isValidWarranty(workOrder)) {
        return false;
    }
    return true;
}

/**
 * validates admin bulk replace work order creation form
 * @param {object} workOrder
 * @returns {boolean}
 */
export function replaceCreationFormIsValid(workOrder, deprecated = false) {
    if (!WorkOrderService.isValid(workOrder, deprecated)) {
        return false;
    }
    if (!workOrder.schedule) {
        return false;
    }
    return true;
}

/**
 * validates admin bulk repair work order creation form
 * @param {object} workOrder
 * @returns {boolean}
 */
export function repairCreationFormIsValid(workOrder, deprecated = false) {
    if (!WorkOrderService.isValid(workOrder, deprecated)) {
        return false;
    }
    if (!workOrder.schedule) {
        return false;
    }
    return true;
}

/**
 *
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionCancel(workOrder) {
    if (!workOrder) {
        return false;
    }
    const acceptableOwnerStatuses = [WORK_ORDER_STATUS.IN_PROCESS];

    if (
        isMotiliOwner() &&
        acceptableOwnerStatuses.includes(workOrder.statusId)
    ) {
        return true;
    }

    const acceptableOwnerAdminStatuses = [
        'SCHEDULING',
        'CONTACT_REQUIRED',
        'DISPATCHING',
        'DISPATCHED',
        'NO_CONTRACTOR_FOUND',
        'DELAYED',
        'CREATED',
        'ORDERED',
        'SCHEDULED',
    ];

    if (
        isMotiliAdmin() &&
        acceptableOwnerAdminStatuses.includes(workOrder.statusId)
    ) {
        return true;
    }

    return false;
}

export function shouldDisplayActionClose(workOrder) {
    return (
        (isMotiliAdmin() || isMotiliFinanceDept()) &&
        workOrder?.statusId === WORK_ORDER_STATUS.COMPLETED
    );
}

/**
 * determines if work order action schedule is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @param {Object[]} workOrder.lineItems
 * @returns {Boolean}
 */
export function shouldDisplayActionSchedule(workOrder) {
    if (!workOrder || workOrder.dispatchingHold) {
        return false;
    }

    const acceptableMotiliAccountWorkOrderStatuses = [
        WORK_ORDER_STATUS.SCHEDULING,
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.DISPATCHING,
    ];

    if (
        isMotiliAccount() &&
        acceptableMotiliAccountWorkOrderStatuses.includes(workOrder.statusId)
    ) {
        return true;
    }

    if (
        workOrder.statusId === WORK_ORDER_STATUS.ORDERED &&
        (workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE)
    ) {
        return true;
    }

    if (
        workOrder.statusId === WORK_ORDER_STATUS.CONTACT_REQUIRED ||
        workOrder.statusId === WORK_ORDER_STATUS.CREATED
    ) {
        return true;
    }

    return false;
}

/**
 * determines if work order action schedule with followup is available
 * @param {Object} workOrder
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionScheduleWithFollowup(workOrder) {
    if (!workOrder) {
        return false;
    }
    if (workOrder.statusId === WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND) {
        return true;
    }
    return false;
}

/**
 * determines if work order action schedule followup with No re dispatch is available
 * @param {Object} workOrder
 * @param {String} workOrder.statusId
 * @return {boolean}
 */
export function shouldDisplayActionScheduleFollowupWithNoDispatch(workOrder) {
    if (!workOrder) {
        return false;
    }
    if (
        workOrder.statusId === WORK_ORDER_STATUS.DISPATCHING &&
        workOrder.schedule &&
        !workOrder.schedule.startDateTime &&
        !workOrder.contractorId
    ) {
        return true;
    }
    return false;
}

/**
 * determines if work order action assign is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionAssign(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (
        isMotiliAccount() &&
        workOrder.statusId === WORK_ORDER_STATUS.DISPATCHING &&
        workOrder?.schedule?.startDateTime
    ) {
        return true;
    }

    if (
        workOrder.statusId === WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND &&
        workOrder?.schedule?.startDateTime
    ) {
        return true;
    }

    return false;
}

/**
 * determines if work order action re-assign is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionReAssign(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (
        isMotiliAdmin() &&
        workOrder.statusId === WORK_ORDER_STATUS.CREATED &&
        workOrder.contractorId
    ) {
        return true;
    }

    if (
        isMotiliAdmin() &&
        workOrder.statusId === WORK_ORDER_STATUS.DISPATCHED
    ) {
        return true;
    }

    if (
        isMotiliAdmin() &&
        workOrder.statusId === WORK_ORDER_STATUS.ORDERED &&
        workOrder.contractorId &&
        (workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE)
    ) {
        return true;
    }

    const documentOrigination =
        workOrder.documentOrigination &&
        workOrder.documentOrigination.documentTypeId;

    if (
        isMotiliAdmin() &&
        workOrder.statusId === WORK_ORDER_STATUS.ORDERED &&
        documentOrigination === 'QUOTE'
    ) {
        return true;
    }

    return false;
}

/**
 * determines if work order action un-assign is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionUnAssign(workOrder) {
    if (!workOrder) {
        return false;
    }
    const acceptableStatuses = [
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.SCHEDULED,
    ];

    if (isMotiliAdmin() && acceptableStatuses.includes(workOrder.statusId)) {
        return true;
    }

    if (
        isMotiliAdmin() &&
        workOrder.statusId === WORK_ORDER_STATUS.ORDERED &&
        workOrder.contractorId &&
        (workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE)
    ) {
        return true;
    }

    return false;
}

/**
 * determines if work order action check in is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @param {Array} workOrder.checkInsV2
 * @returns {Boolean}
 */
export function shouldDisplayActionCheckIn(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (
        workOrder.statusId === WORK_ORDER_STATUS.DISPATCHED ||
        workOrder.statusId === WORK_ORDER_STATUS.DELAYED
    ) {
        return true;
    }

    if (workOrder.statusId === WORK_ORDER_STATUS.SCHEDULED) {
        if (
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.WARRANTY ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.DIAGNOSTIC ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.WALKTHROUGH ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.INSPECTION ||
            workOrder.workOrderTypeId ===
                WORK_ORDER_TYPE.PREVENTATIVE_MAINTENANCE
        ) {
            return true;
        }
    }

    return workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS;
}

/**
 * determines if work order action check out is available
 * @param {Object} workOrder
 * @param {Array} workOrder.checkInsV2
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionCheckOut(workOrder) {
    if (!workOrder) {
        return false;
    }
    return (
        (workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS ||
            workOrder.statusId === WORK_ORDER_STATUS.IN_REVIEW) &&
        workOrder.checkInsV2.filter(checkIn => !checkIn.checkedOutAt).length
    );
}

/**
 * determines if work order action submit is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionSubmit(workOrder) {
    if (!workOrder) {
        return false;
    }

    const hasCheckedInAndCheckedOut =
        workOrder.checkInsV2.length > 0 &&
        workOrder.checkInsV2.some(checkIn => checkIn.checkedOutAt);
    return (
        workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS &&
        hasCheckedInAndCheckedOut
    );
}

/**
 * determines if work order action submit LHS Risk is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionSubmitLHSRisk(workOrder) {
    if (!workOrder) {
        return false;
    }

    const validStatus = [
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.IN_PROCESS,
    ];

    return validStatus.includes(workOrder.statusId);
}

/**
 * determines if work order action delay is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionDelay(workOrder) {
    if (!workOrder || workOrder.dispatchingHold) {
        return false;
    }
    const acceptableStatuses = [
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.IN_PROCESS,
    ];

    if (isMotiliAccount() && acceptableStatuses.includes(workOrder.statusId)) {
        return true;
    }

    if (
        isMotiliAccount() &&
        workOrder.statusId === WORK_ORDER_STATUS.ORDERED &&
        workOrder.contractorId &&
        (workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE)
    ) {
        return true;
    }

    const documentOrigination =
        workOrder.documentOrigination &&
        workOrder.documentOrigination.documentTypeId;

    if (
        isMotiliAccount() &&
        workOrder.statusId === WORK_ORDER_STATUS.ORDERED &&
        documentOrigination === 'QUOTE'
    ) {
        return true;
    }

    return false;
}

export function shouldDisplayActionDelayDispatching(workOrder) {
    if (!workOrder || workOrder.dispatchingHold) {
        return false;
    }
    return (
        isMotiliAccount() &&
        workOrder.statusId === WORK_ORDER_STATUS.CREATED &&
        !workOrder.contractorId
    );
}

export function shouldDisplayActionEditDispatching(workOrder) {
    if (!workOrder) {
        return false;
    }
    return isMotiliAccount() && !!workOrder.dispatchingHold;
}

/**
 * determines if work order action create quote is available
 * @param {Object} workOrder
 * @param {String} workOrder.statusId
 * @param {Object} workOrder.sequenceElement
 * @param {Boolean} worKOrder.sequenceElement.active
 * @param {Object} workOrder.sequenceElement.nextDocumentTypeId
 * @param {String} workOrder.sequenceElement.nextDocumentId
 * @returns {Boolean}
 */
export function shouldDisplayActionCreateQuote(workOrder) {
    if (!workOrder) {
        return false;
    }
    if (workOrder.property && !isValidPropertyStatus(workOrder.property)) {
        return false;
    }
    const workOrderTypes = [
        WORK_ORDER_TYPE.DIAGNOSTIC,
        WORK_ORDER_TYPE.WALKTHROUGH,
        WORK_ORDER_TYPE.WARRANTY,
        WORK_ORDER_TYPE.PREVENTATIVE_MAINTENANCE,
    ];
    if (workOrderTypes.includes(workOrder.workOrderTypeId)) {
        const acceptableStatuses = [
            WORK_ORDER_STATUS.COMPLETED,
            WORK_ORDER_STATUS.CLOSED,
        ];

        if (acceptableStatuses.includes(workOrder.statusId)) {
            if (
                workOrder.sequenceElement.active &&
                !workOrder.sequenceElement.nextDocumentTypeId &&
                !workOrder.sequenceElement.nextDocumentId
            ) {
                return true;
            }
        }
    }
    return false;
}

export function shouldDisplayManageAction(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (
        isMotiliAccount() &&
        workOrder.statusId !== WORK_ORDER_STATUS.CLOSED &&
        workOrder.statusId !== WORK_ORDER_STATUS.CANCELLED &&
        workOrder.statusId !== WORK_ORDER_STATUS.DRAFTED &&
        workOrder.statusId !== WORK_ORDER_STATUS.DRAFT_CANCELLED
    ) {
        return true;
    }

    return false;
}

export function shouldDisplayActionComplete(workOrder) {
    if (!workOrder) {
        return false;
    }
    return workOrder.statusId === WORK_ORDER_STATUS.IN_REVIEW;
}

export function shouldDisplayActionQualityApproval(workOrder) {
    if (!workOrder) {
        return false;
    }
    return (
        workOrder.issueId &&
        workOrder.issueId.includes('QUALITY_APPROVAL_REQUIRED')
    );
}

export function shouldDisplayActionReOpen(workOrder) {
    if (!workOrder) {
        return false;
    }
    return workOrder.statusId === WORK_ORDER_STATUS.IN_REVIEW;
}

export function shouldDisplayActionChangeProject(workOrder) {
    if (!workOrder) {
        return false;
    }
    if (
        isMotiliAccount() &&
        workOrder.statusId !== WORK_ORDER_STATUS.CLOSED &&
        workOrder.statusId !== WORK_ORDER_STATUS.CANCELLED &&
        workOrder.statusId !== WORK_ORDER_STATUS.DISPATCHING
    ) {
        return true;
    }
    return false;
}
export function shouldDisplayActionChangeBilledTo(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (
        isMotiliAccount() &&
        workOrder.workOrderTypeId ===
            WORK_ORDER_TYPE.PREVENTATIVE_MAINTENANCE &&
        workOrder.client.agentBilling &&
        workOrder.statusId !== WORK_ORDER_STATUS.CLOSED &&
        workOrder.statusId !== WORK_ORDER_STATUS.CANCELLED
    ) {
        return true;
    }

    return false;
}

export function shouldDisplayActionChangeSubCategory() {
    return isMotiliAccount();
}

export function shouldDisplayActionChangeTradeType(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (isMotiliAccount() && workOrder.statusId === WORK_ORDER_STATUS.DRAFTED) {
        return true;
    }

    return false;
}

export function shouldDisplayActionChangeWorkOrderType(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (isMotiliAccount() && workOrder.statusId === WORK_ORDER_STATUS.DRAFTED) {
        return true;
    }

    return false;
}

export function shouldDisplayActionChangeClipLevel(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (isMotiliAccount() && workOrder.statusId === WORK_ORDER_STATUS.DRAFTED) {
        return true;
    }

    return false;
}
/**
 * determines if line item action remove is available
 * @param {String} workOrderStatusId
 * @param {String} lineItemStatusId
 * @param {String} bundleJobLineItemId
 * @param {String} productCategoryId
 * @param {array} bundleLineItems
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionRemove(
    workOrderStatusId,
    lineItemStatusId,
    bundleJobLineItemId,
    productCategoryId,
    bundleLineItems = []
) {
    if (!workOrderStatusId || !lineItemStatusId) {
        return false;
    }

    const acceptableWorkOrderStatuses = [...baseAcceptableWorkOrderStatuses];

    if (
        lineItemStatusId === 'CREATED' &&
        (bundleLineItems.length === 0 ||
            !some(bundleLineItems, item => item.statusId !== 'CREATED'))
    ) {
        if (
            isMotiliAccount() &&
            acceptableWorkOrderStatuses.includes(workOrderStatusId)
        ) {
            return true;
        }

        if (
            (isMotiliAccount() || isContractorAccount()) &&
            [
                WORK_ORDER_STATUS.IN_PROCESS,
                WORK_ORDER_STATUS.IN_REVIEW,
            ].includes(workOrderStatusId)
        ) {
            return true;
        }
    }
    return false;
}

/**
 * determines if line item action approve is available
 * @param {String} workOrderStatusId
 * @param {String} lineItemStatusId
 * @param {Object} bundleJobLineItemId
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionApprove(
    workOrderStatusId,
    lineItemStatusId,
    bundleJobLineItemId = null
) {
    if (!workOrderStatusId || !lineItemStatusId) {
        return false;
    }

    if (bundleJobLineItemId) {
        return false;
    }

    const acceptableWorkOrderStatuses = [
        ...baseAcceptableWorkOrderStatuses,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.IN_REVIEW,
    ];

    if (
        isMotiliAdmin() &&
        lineItemStatusId === 'CREATED' &&
        acceptableWorkOrderStatuses.includes(workOrderStatusId)
    ) {
        return true;
    }
    return false;
}

/**
 * determines if line item action unapprove is available
 * @param {String} workOrderStatusId
 * @param {String} lineItemStatusId
 * @param {Object} bundleJobLineItemId
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionUnapprove(
    workOrderStatusId,
    lineItemStatusId,
    bundleJobLineItemId = null,
    bundleLineItems = []
) {
    if (!workOrderStatusId || !lineItemStatusId) {
        return false;
    }

    if (bundleJobLineItemId) {
        return false;
    }

    const acceptableWorkOrderStatuses = [
        ...baseAcceptableWorkOrderStatuses,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.IN_REVIEW,
    ];

    if (
        isMotiliAdmin() &&
        lineItemStatusId === 'APPROVED' &&
        (bundleLineItems.length === 0 ||
            !some(bundleLineItems, item => item.statusId !== 'APPROVED')) &&
        acceptableWorkOrderStatuses.includes(workOrderStatusId)
    ) {
        return true;
    }

    return false;
}

/**
 * determines if line item action edit is available
 * @param {String} workOrderStatusId
 * @param {String} lineItemStatusId
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionEdit(
    workOrderStatusId,
    lineItemStatusId
) {
    if (!workOrderStatusId || !lineItemStatusId) {
        return false;
    }

    const acceptableWorkOrderStatuses = [...baseAcceptableWorkOrderStatuses];

    if (
        acceptableWorkOrderStatuses.includes(workOrderStatusId) &&
        ((isMotiliAccount() && lineItemStatusId === 'CREATED') ||
            (isMotiliAdmin() && lineItemStatusId === 'APPROVED'))
    ) {
        return true;
    }

    if (
        isMotiliAdmin() &&
        (workOrderStatusId === WORK_ORDER_STATUS.IN_PROCESS ||
            workOrderStatusId === WORK_ORDER_STATUS.IN_REVIEW)
    ) {
        const acceptableLineItemStatuses = ['CREATED', 'APPROVED'];

        if (acceptableLineItemStatuses.includes(lineItemStatusId)) {
            return true;
        }
    }

    return false;
}

/**
 * determines if line item can edit quantity
 * @param {array} lineItems
 * @param {object} lineItem
 * @returns {Boolean}
 */
export function shouldAllowEditLineItemQuantity(lineItems, lineItem) {
    if (
        lineItem.productCategoryId === 'BUNDLE' &&
        lineItems
            .filter(li => li.bundleJobLineItemId === lineItem.id)
            .some(li =>
                [
                    'COMPLETED',
                    'PARTIALLY_RETURNED',
                    'RETURNED',
                    'RETURNING',
                ].includes(li.statusId)
            )
    ) {
        return false;
    }
    return !lineItem.bundleJobLineItemId;
}

/**
 * determines if line item action substitute is available
 * @param {String} workOrderStatusId
 * @param {String} lineItemStatusId
 * @param {Object} lineItem
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionSubstitute(
    workOrderStatusId,
    lineItemStatusId,
    lineItem
) {
    if (!workOrderStatusId || !lineItemStatusId || !lineItem) {
        return false;
    }

    const acceptableWorkOrderStatuses = [
        WORK_ORDER_STATUS.CREATED,
        WORK_ORDER_STATUS.SCHEDULING,
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.CONTACT_REQUIRED,
        WORK_ORDER_STATUS.DISPATCHING,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.IN_REVIEW,
        WORK_ORDER_STATUS.DRAFTED,
    ];

    const acceptableProductCategoryIds = ['EQUIPMENT', 'PART', 'LABOR'];
    const acceptableProductSubCategoryIds = ['EQUIPMENT', 'PART'];
    const acceptableLineItemStatuses = ['CREATED', 'APPROVED'];
    if (
        !isMotiliAccount() ||
        !acceptableWorkOrderStatuses.includes(workOrderStatusId) ||
        !acceptableLineItemStatuses.includes(lineItemStatusId) ||
        !lineItem.bundleJobLineItemId
    ) {
        return false;
    }
    if (
        lineItem.productCategoryId === 'CUSTOM' &&
        acceptableProductSubCategoryIds.includes(lineItem.productSubCategoryId)
    ) {
        return true;
    }
    return acceptableProductCategoryIds.includes(lineItem.productCategoryId);
}

/**
 * determines if work order action transferOwner is available
 * @param {Object} workOrder
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionTransferOwner(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (
        workOrder.statusId !== WORK_ORDER_STATUS.CLOSED &&
        workOrder.statusId !== WORK_ORDER_STATUS.CANCELLED
    ) {
        return true;
    }

    return false;
}

export function shouldDisplayActionChangePriority(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (
        isMotiliAccount() &&
        workOrder.statusId !== WORK_ORDER_STATUS.CLOSED &&
        workOrder.statusId !== WORK_ORDER_STATUS.CANCELLED &&
        workOrder.statusId !== WORK_ORDER_STATUS.COMPLETED &&
        ![
            WORK_ORDER_TYPE.WALKTHROUGH,
            WORK_ORDER_TYPE.PREVENTATIVE_MAINTENANCE,
        ].includes(workOrder.workOrderTypeId)
    ) {
        return true;
    }
    return false;
}

export function shouldDisplayActionReschedule(workOrder) {
    if (!workOrder) {
        return false;
    }
    if (workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS) return true;

    return false;
}

export function shouldDisplayActionApproveDraft(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (isMotiliOwner() && workOrder.statusId === WORK_ORDER_STATUS.DRAFTED) {
        return true;
    }

    return false;
}

export function shouldDisplayActionClone(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (
        isMotiliAccount() &&
        workOrder.statusId === WORK_ORDER_STATUS.CANCELLED &&
        workOrder.workOrderTypeId !== WORK_ORDER_TYPE.WALKTHROUGH
    ) {
        return !workOrder.events.find(event => event.action === 'CLONE');
    }

    return false;
}

export function shouldDisplayActionCancelDraft(workOrder) {
    if (!workOrder) {
        return false;
    }

    if (isMotiliOwner() && workOrder.statusId === WORK_ORDER_STATUS.DRAFTED) {
        return true;
    }

    return false;
}

export function updateReferenceNumber(workOrderId, referenceToUpdate) {
    if (!workOrderId)
        return Promise.reject(new Error('Work Order Id is required'));
    return WorkOrderService.updateReferenceNumber(
        workOrderId,
        referenceToUpdate
    );
}

export function getDocumentByJobFilter(filterQuery = {}) {
    return DocumentService.getDocumentByJobFilter('jobs', {
        fields: ['id'],
        where: filterQuery,
    });
}

export function getPriorityTypes() {
    return ApiService.get(true, 'WorkOrders/priorities').then(res =>
        res.map(item => ({ display: item.display, id: item.id }))
    );
}
export function getPriorityChangeReasonTypes() {
    return ApiService.get(true, 'WorkOrders/priorityChangeReasons').then(res =>
        res.map(item => ({ display: item.display, id: item.id }))
    );
}

export function changeProject(id, projectId) {
    return WorkOrderService.changeProject(id, projectId);
}

export function getDownloadAllPhotoUrl(id) {
    return ApiService.get(true, `WorkOrders/${id}/downloadAllPhoto`);
}

/**
 *
 * @param {number} id
 * @returns {Promise<Object>}
 */
export function approveDraft(id) {
    return ApiService.post(true, `WorkOrders/${id}/approveDraft`);
}

/**
 *
 * @param {number} id
 * @returns {Promise<Object>}
 */
export function cancelDraft(id) {
    return ApiService.post(true, `WorkOrders/${id}/cancelDraft`);
}

export function updateDraft(id, data) {
    return ApiService.patch(true, `WorkOrders/${id}/updateDraft`, data);
}

export function cloneFromCancel(id, reason) {
    const query = { reason };
    return ApiService.post(true, `WorkOrders/${id}/cloneFromCancel`, {}, query);
}

export async function financeApproveEdit(document) {
    const updateLineItems = await getFinanceUpdateLineItems(document);
    const data = {
        ...updateLineItems,
        adjustments: document.adjustments,
        adjustmentReason: document.adjustmentReason,
        adjustmentReasonNote: document.adjustmentReasonNote,
    };

    return ApiService.post(
        true,
        `WorkOrders/${document.id}/approveFinanceEdit`,
        data
    );
}

export function contactRequired(id) {
    return ApiService.post(
        true,
        `WorkOrders/${id}/contactRequired`,
        undefined,
        {}
    );
}

export function getLineItemSummary(workOrderId) {
    return ApiService.get(true, `WorkOrders/${workOrderId}/lineItemSummary`);
}

export function calculateTax(workOrderId) {
    return ApiService.post(true, `WorkOrders/${workOrderId}/calculateTax`);
}

export function shouldDisplayActionFinanceEdit(workOrder) {
    if (!workOrder) {
        return false;
    }
    const statusesAllowResync = ['COMPLETED', 'CLOSED'];
    if (
        isMotiliApFinanceDept() &&
        statusesAllowResync.includes(workOrder.statusId)
    ) {
        return true;
    }
    return false;
}

export function isAllowResync(id) {
    return ApiService.get(true, `WorkOrders/${id}/isAllowResync`);
}

export function declineOnBehalf(workOrderId, contractorId, teamMemberId) {
    const declineInfo = {
        workOrderId,
        contractorId,
        contractorTeamMemberId: teamMemberId,
    };
    return WorkOrderService.declineOnBehalf(workOrderId, declineInfo);
}

export function applyTechScheduleAcls(workOrder) {
    const statusId = workOrder?.statusId;

    return statusId === WORK_ORDER_STATUS.DRAFTED;
}

export function applyExternalAcls(workOrder) {
    const statusId = workOrder?.statusId;

    const statuses = [
        ...baseAcceptableWorkOrderStatuses,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.IN_REVIEW,
    ];

    if (statuses.includes(statusId)) return true;

    if (
        statusId === WORK_ORDER_STATUS.COMPLETED ||
        statusId === WORK_ORDER_STATUS.CLOSED ||
        statusId === WORK_ORDER_STATUS.CANCELLED
    ) {
        return false;
    }

    return false;
}

export function applySpecialInstructionsAcls(workOrder) {
    const statusId = workOrder?.statusId;

    const statuses = [
        ...baseAcceptableWorkOrderStatuses,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.IN_REVIEW,
    ];

    if (statuses.includes(statusId)) {
        return true;
    }

    if (isMotiliOwner() && statusId === WORK_ORDER_STATUS.COMPLETED) {
        return true;
    }

    if (
        statusId === WORK_ORDER_STATUS.CLOSED ||
        statusId === WORK_ORDER_STATUS.CANCELLED
    ) {
        return false;
    }

    return false;
}

export function applyContactAcls(workOrder) {
    const statusId = workOrder?.statusId;

    const statuses = [
        WORK_ORDER_STATUS.DRAFTED,
        WORK_ORDER_STATUS.CONTACT_REQUIRED,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.DISPATCHING,
        WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND,
        WORK_ORDER_STATUS.IN_REVIEW,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.CREATED,
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.ORDERED,
    ];

    if (statuses.includes(statusId)) {
        return true;
    }

    if (
        isMotiliOwner() &&
        (statusId === WORK_ORDER_STATUS.SCHEDULING ||
            statusId === WORK_ORDER_STATUS.COMPLETED)
    ) {
        return true;
    }

    if (
        statusId === WORK_ORDER_STATUS.CLOSED ||
        statusId === WORK_ORDER_STATUS.CANCELLED
    ) {
        return false;
    }

    return false;
}

export function applyEditPropertyAcls(workOrder) {
    const statusId = workOrder?.statusId;

    return statusId === WORK_ORDER_STATUS.DRAFTED;
}

export function shouldDisplayActionContactRequired(workOrder) {
    if (!isOccupiedWorkOrder(workOrder)) {
        return false;
    }
    const acceptableStatuses = [
        WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND,
        WORK_ORDER_STATUS.DISPATCHING,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.SCHEDULING,
        WORK_ORDER_STATUS.SCHEDULED,
    ];

    if (!acceptableStatuses.includes(workOrder?.statusId)) {
        return false;
    }

    if (!isMotiliAccount()) {
        return false;
    }

    return true;
}

// whether or not to display the sub actions from the FLAG action that can be added on a work order with status NCF
export function shouldDisplaySubActionFlagNCF(workOrder) {
    const { statusId } = workOrder;

    return statusId === WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND;
}

export function shouldDisplayActionFlag(workOrder) {
    const statusId = workOrder.statusId;

    return (
        isMotiliAccount() &&
        statusId &&
        statusId !== WORK_ORDER_STATUS.CANCELLED &&
        statusId !== WORK_ORDER_STATUS.CLOSED &&
        statusId !== WORK_ORDER_STATUS.DRAFT_CANCELLED
    );
}

export function shouldDisplayActionManageContractor(workOrder) {
    const { statusId } = workOrder;

    const acceptableStatuses = [
        WORK_ORDER_STATUS.CREATED,
        WORK_ORDER_STATUS.CONTACT_REQUIRED,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.DISPATCHING,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND,
        WORK_ORDER_STATUS.ORDERED,
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.SCHEDULING,
        WORK_ORDER_STATUS.IN_REVIEW,
    ];

    return (
        isMotiliAccount() &&
        statusId &&
        acceptableStatuses.includes(statusId) &&
        !workOrder.dispatchingHold
    );
}

function isOccupiedWorkOrder(workOrder) {
    if (workOrder?.occupied) {
        return true;
    }
    if (
        workOrder?.tradeTypeId === 'HVAC' &&
        workOrder?.tradeSubTypeId === 'COMMERCIAL'
    ) {
        return true;
    }
    return false;
}

export async function getWorkOrderIdsByIssue(
    issueForms = [],
    issueReasonForms = [],
    issueSecondaryReasonForms = []
) {
    const _conditions = map(issueForms, issueIdForm => {
        const _condition = {
            and: [{ issueId: issueIdForm.id }, { active: true }],
        };
        const relateReasonIds = map(issueIdForm.reasons, res => res.id);
        const relateReasonForms = _filter(issueReasonForms, issueReasonForm =>
            relateReasonIds.includes(issueReasonForm.id)
        );
        if (relateReasonForms && relateReasonForms.length > 0) {
            const reasonClause = {
                or: [],
            };
            relateReasonForms.forEach(relReaForm =>
                reasonClause.or.push({
                    issueReasonId: { like: `%${relReaForm.id}%` },
                })
            );

            _condition.and.push(reasonClause);
            const relateSecondaryReasonForms = _filter(
                issueSecondaryReasonForms,
                issueSecondaryForm => {
                    for (let i = 0; i < relateReasonForms.length; i++) {
                        if (
                            some(relateReasonForms[i].reasons, {
                                id: issueSecondaryForm.id,
                            })
                        ) {
                            return true;
                        }
                    }
                    return false;
                }
            );
            if (
                relateSecondaryReasonForms &&
                relateSecondaryReasonForms.length > 0
            ) {
                const secondaryClause = {
                    or: [],
                };
                relateSecondaryReasonForms.forEach(relSecReaForm =>
                    secondaryClause.or.push({
                        issueSecondaryReasonId: {
                            like: `%${relSecReaForm.id}%`,
                        },
                    })
                );
                _condition.and.push(secondaryClause);
            }
        }

        return _condition;
    });

    const filter = {
        fields: ['workOrderId'],
        where: { and: [{ or: _conditions }] },
    };

    const issueTracking = await fetchWorkOrderIssueTracking(filter);
    return uniq(map(issueTracking, i => i.workOrderId));
}

export function needsScheduleAssign(
    workOrder,
    contractorId,
    contractorTeamMemberId
) {
    return (
        workOrder.statusId === WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND &&
        contractorId &&
        contractorTeamMemberId
    );
}

export function shouldAllowManageAsset(workOrder) {
    if (!workOrder) return false;

    return [
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.IN_REVIEW,
    ].includes(workOrder.statusId);
}

/**
 * retrieves all workOrder related notes
 * @param {number} jobId
 * @returns {Promise<Array>}
 */

export function getWorkOrderRelatedNotes(jobId) {
    return WorkOrderService.getWorkOrderRelatedNotes(jobId);
}

/**
 * retrieves all workOrder related comments
 * @param {number} jobId
 * @returns {Promise<Array>}
 */
export function getWorkOrderRelatedComments(jobId) {
    return WorkOrderService.getWorkOrderRelatedComments(jobId);
}
